import { useEffect, useState, useRef, useMemo } from "react";
import Swal from "sweetalert2";
import ReactQuill from "react-quill-new";
import { usePagination } from "../hooks/usePagination";
import UniversalPagination from "../common/UniversalPagination";
import "react-quill-new/dist/quill.snow.css";
import { API_BASE } from "../config/api";

import Sidebar from '../components/Sidebar'
import Header from '../components/Header'

function VideoList() {

    const [instructors, setInstructors] = useState([]);
    const [loadingInstructors, setLoadingInstructors] = useState(false);
    const [savingVideo, setSavingVideo] = useState(false);
    const [videos, setVideos] = useState([]);
    const [loadingVideos, setLoadingVideos] = useState(false);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [loadingViewVideo, setLoadingViewVideo] = useState(false);
    const [loadingEditVideo, setLoadingEditVideo] = useState(false);
    const [tagsCatalog, setTagsCatalog] = useState([]);
    const [loadingTags, setLoadingTags] = useState(false);

    const [selectedTags, setSelectedTags] = useState([]);
    const [editSelectedTags, setEditSelectedTags] = useState([]);

    const [videoForm, setVideoForm] = useState({
        Video_Titulo: "",
        Instructor_ID: "",
        Video_Descripcion: "",
        Video_URL_Vimeo: "",
        Video_Duracion_Seg: "",
        Video_Tiene_Evaluacion: 0,
        Video_Miniatura: null,
        Video_Miniatura_Sitio: null,
    });

    const [successMessage, setSuccessMessage] = useState("");
    const [infoMessage, setInfoMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    const resetMessages = () => {
        setSuccessMessage("");
        setInfoMessage("");
        setErrorMessage("");
    };

    const resetVideoForm = () => {
        setVideoForm({
            Video_Titulo: "",
            Instructor_ID: "",
            Video_Descripcion: "",
            Video_URL_Vimeo: "",
            Video_Duracion_Seg: "",
            Video_Tiene_Evaluacion: 0,
            Video_Miniatura: null,
            Video_Miniatura_Sitio: null,
        });

        setSelectedTags([]);
        setEvaluacionForm(getInitialEvaluacionForm("", ""));

        const miniaturaInput = document.getElementById("customFileVideo");
        const miniaturaSitioInput = document.getElementById("customFileVideoSite");

        if (miniaturaInput) miniaturaInput.value = "";
        if (miniaturaSitioInput) miniaturaSitioInput.value = "";
    };

    const loadVideoTags = async () => {
        try {
            setLoadingTags(true);
            setErrorMessage("");

            const response = await fetch(`${API_BASE}/api/admin/videoList/tags`, {
                method: "GET",
                credentials: "include",
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data?.message || "No fue posible obtener los tags");
            }

            setTagsCatalog(data?.data || []);
        } catch (error) {
            console.error("Error al cargar tags:", error);
            setErrorMessage(error.message || "Ocurrió un error al cargar los tags");
        } finally {
            setLoadingTags(false);
        }
    };

    const loadVideos = async () => {
        try {
            setLoadingVideos(true);
            setErrorMessage("");

            const response = await fetch(`${API_BASE}/api/admin/videoList`, {
                method: "GET",
                credentials: "include",
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data?.message || "No fue posible obtener los videos");
            }

            setVideos(data?.data || []);
        } catch (error) {
            console.error("Error al cargar videos:", error);
            setErrorMessage(error.message || "Ocurrió un error al cargar los videos");
        } finally {
            setLoadingVideos(false);
        }
    };

    useEffect(() => {
        loadVideoInstructors();
        loadVideoTags();
        loadVideos();
    }, []);

    const loadVideoInstructors = async () => {
        try {
            setLoadingInstructors(true);
            setErrorMessage("");

            const response = await fetch(`${API_BASE}/api/admin/videoList/instructors`, {
                method: "GET",
                credentials: "include",
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data?.message || "No fue posible obtener los instructores");
            }

            setInstructors(data?.data || []);
        } catch (error) {
            console.error("Error al cargar instructores:", error);
            setErrorMessage(error.message || "Ocurrió un error al cargar los instructores");
        } finally {
            setLoadingInstructors(false);
        }
    };

    const renderVideoStatus = (videoActivo) => {
        if (Number(videoActivo) === 1) {
            return (
                <span className="badge bg-success-subtle p-2 text-success">
                    Activo
                </span>
            );
        }

        return (
            <span className="badge bg-danger-subtle p-2 text-danger">
                Inactivo
            </span>
        );
    };

    const handleTagToggle = (tagId) => {
        setSelectedTags((prev) => {
            const exists = prev.includes(tagId);

            if (exists) {
                return prev.filter((id) => id !== tagId);
            }

            return [...prev, tagId];
        });
    };

    const handleEditTagToggle = (tagId) => {
        setEditSelectedTags((prev) => {
            const exists = prev.includes(tagId);

            if (exists) {
                return prev.filter((id) => id !== tagId);
            }

            return [...prev, tagId];
        });
    };

    const handleVideoInputChange = (e) => {
        const { name, value, type, checked, files } = e.target;

        if (type === "file") {
            setVideoForm((prev) => ({
                ...prev,
                [name]: files?.[0] || null,
            }));
            return;
        }

        if (type === "checkbox") {
            const checkedValue = checked ? 1 : 0;

            setVideoForm((prev) => ({
                ...prev,
                [name]: checkedValue,
            }));

            if (name === "Video_Tiene_Evaluacion") {
                if (checkedValue === 1) {
                    setEvaluacionForm((prev) => {
                        const hasPreguntas =
                            Array.isArray(prev?.preguntas) && prev.preguntas.length > 0;

                        return hasPreguntas
                            ? {
                                ...prev,
                                Evaluacion_Titulo: videoForm.Video_Titulo?.trim()
                                    ? `Evaluación - ${videoForm.Video_Titulo.trim()}`
                                    : "Evaluación - ",
                                Evaluacion_Descripcion: videoForm.Video_Descripcion || "",
                            }
                            : getInitialEvaluacionForm(
                                videoForm.Video_Titulo,
                                videoForm.Video_Descripcion
                            );
                    });
                } else {
                    setEvaluacionForm(
                        getInitialEvaluacionForm(
                            videoForm.Video_Titulo,
                            videoForm.Video_Descripcion
                        )
                    );
                }
            }

            return;
        }

        setVideoForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleDescriptionChange = (value) => {
        setVideoForm((prev) => ({
            ...prev,
            Video_Descripcion: value,
        }));
    };

    const validateVideoForm = () => {
        if (!videoForm.Video_Titulo.trim()) {
            setInfoMessage("El campo título es obligatorio.");
            return false;
        }

        if (!videoForm.Instructor_ID) {
            setInfoMessage("El campo experto es obligatorio.");
            return false;
        }

        if (!videoForm.Video_Descripcion || !videoForm.Video_Descripcion.trim() || videoForm.Video_Descripcion === "<p><br></p>") {
            setInfoMessage("El campo descripción es obligatorio.");
            return false;
        }

        if (!videoForm.Video_Miniatura) {
            setInfoMessage("La miniatura del video es obligatoria.");
            return false;
        }

        if (!videoForm.Video_Miniatura_Sitio) {
            setInfoMessage("La miniatura para el sitio es obligatoria.");
            return false;
        }

        if (!videoForm.Video_URL_Vimeo.trim()) {
            setInfoMessage("El campo ID Vimeo es obligatorio.");
            return false;
        }

        if (!videoForm.Video_Duracion_Seg.trim()) {
            setInfoMessage("El campo duración aproximada es obligatorio.");
            return false;
        }

        if (!/^\d+$/.test(videoForm.Video_Duracion_Seg.trim())) {
            setInfoMessage("La duración aproximada debe ser un número entero.");
            return false;
        }

        if (!validateEvaluacionForm()) {
            return false;
        }

        return true;
    };

    // Funcion para agregar evaluaciones

    const getEmptyRespuesta = (orden = 1) => ({
        Respuesta_Texto: "",
        Respuesta_Orden: orden,
        Respuesta_Correcta: 0,
    });

    const getEmptyPregunta = (orden = 1) => ({
        Pregunta_Texto: "",
        Pregunta_Orden: orden,
        Pregunta_Puntos_Estreno: 1,
        Pregunta_Puntos_Normal: 1,
        respuestas: [
            getEmptyRespuesta(1),
            getEmptyRespuesta(2),
            getEmptyRespuesta(3),
            getEmptyRespuesta(4),
        ],
    });

    const getInitialEvaluacionForm = (videoTitulo = "", videoDescripcion = "") => ({
        Evaluacion_Titulo: videoTitulo?.trim()
            ? `Evaluación - ${videoTitulo.trim()}`
            : "Evaluación - ",
        Evaluacion_Descripcion: videoDescripcion || "",
        preguntas: [getEmptyPregunta(1), getEmptyPregunta(2)],
    });

    const [evaluacionForm, setEvaluacionForm] = useState(
        getInitialEvaluacionForm("", "")
    );

    // Este efecto logra que el titulo y descripcion se sincronocen con lo que escribireron en agregar video

    useEffect(() => {
        if (Number(videoForm.Video_Tiene_Evaluacion) !== 1) return;

        setEvaluacionForm((prev) => ({
            ...prev,
            Evaluacion_Titulo: videoForm.Video_Titulo?.trim()
                ? `Evaluación - ${videoForm.Video_Titulo.trim()}`
                : "Evaluación - ",
            Evaluacion_Descripcion: videoForm.Video_Descripcion || "",
        }));
    }, [
        videoForm.Video_Tiene_Evaluacion,
        videoForm.Video_Titulo,
        videoForm.Video_Descripcion,
    ]);

    // Funcion para preguntas 
    const handleEvaluacionFieldChange = (e) => {
        const { name, value } = e.target;

        setEvaluacionForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handlePreguntaChange = (preguntaIndex, field, value) => {
        setEvaluacionForm((prev) => {
            const preguntas = [...prev.preguntas];
            preguntas[preguntaIndex] = {
                ...preguntas[preguntaIndex],
                [field]:
                    field === "Pregunta_Orden" ||
                    field === "Pregunta_Puntos_Estreno" ||
                    field === "Pregunta_Puntos_Normal"
                        ? value === ""
                            ? ""
                            : Number(value)
                        : value,
            };

            return {
                ...prev,
                preguntas,
            };
        });
    };

    const handleRespuestaChange = (preguntaIndex, respuestaIndex, value) => {
        setEvaluacionForm((prev) => {
            const preguntas = [...prev.preguntas];
            const respuestas = [...preguntas[preguntaIndex].respuestas];

            respuestas[respuestaIndex] = {
                ...respuestas[respuestaIndex],
                Respuesta_Texto: value,
            };

            preguntas[preguntaIndex] = {
                ...preguntas[preguntaIndex],
                respuestas,
            };

            return {
                ...prev,
                preguntas,
            };
        });
    };

    const handleCorrectaChange = (preguntaIndex, respuestaIndex) => {
        setEvaluacionForm((prev) => {
            const preguntas = [...prev.preguntas];
            const respuestas = preguntas[preguntaIndex].respuestas.map((respuesta, index) => ({
                ...respuesta,
                Respuesta_Correcta: index === respuestaIndex ? 1 : 0,
            }));

            preguntas[preguntaIndex] = {
                ...preguntas[preguntaIndex],
                respuestas,
            };

            return {
                ...prev,
                preguntas,
            };
        });
    };

    const addPregunta = () => {
        setEvaluacionForm((prev) => ({
            ...prev,
            preguntas: [
                ...prev.preguntas,
                getEmptyPregunta(prev.preguntas.length + 1),
            ],
        }));
    };

    const removePregunta = (preguntaIndex) => {
        setEvaluacionForm((prev) => {
            const preguntas = prev.preguntas
                .filter((_, index) => index !== preguntaIndex)
                .map((pregunta, index) => ({
                    ...pregunta,
                    Pregunta_Orden: index + 1,
                }));

            return {
                ...prev,
                preguntas: preguntas.length > 0 ? preguntas : [getEmptyPregunta(1)],
            };
        });
    };

    const validateEvaluacionForm = () => {
        if (Number(videoForm.Video_Tiene_Evaluacion) !== 1) return true;

        if (!evaluacionForm.Evaluacion_Titulo.trim()) {
            setInfoMessage("El título de la evaluación es obligatorio.");
            return false;
        }

        if (!Array.isArray(evaluacionForm.preguntas) || evaluacionForm.preguntas.length < 1) {
            setInfoMessage("Debes agregar al menos una pregunta.");
            return false;
        }

        for (let i = 0; i < evaluacionForm.preguntas.length; i += 1) {
            const pregunta = evaluacionForm.preguntas[i];

            if (!pregunta.Pregunta_Texto.trim()) {
                setInfoMessage(`La pregunta ${i + 1} debe tener texto.`);
                return false;
            }

            if (
                pregunta.Pregunta_Orden === "" ||
                pregunta.Pregunta_Orden === null ||
                Number(pregunta.Pregunta_Orden) < 1
            ) {
                setInfoMessage(`La pregunta ${i + 1} debe tener un orden válido.`);
                return false;
            }

            if (
                pregunta.Pregunta_Puntos_Estreno === "" ||
                pregunta.Pregunta_Puntos_Estreno === null ||
                Number(pregunta.Pregunta_Puntos_Estreno) < 0
            ) {
                setInfoMessage(`La pregunta ${i + 1} debe tener puntos de estreno válidos.`);
                return false;
            }

            if (
                pregunta.Pregunta_Puntos_Normal === "" ||
                pregunta.Pregunta_Puntos_Normal === null ||
                Number(pregunta.Pregunta_Puntos_Normal) < 0
            ) {
                setInfoMessage(`La pregunta ${i + 1} debe tener puntos normales válidos.`);
                return false;
            }

            if (!Array.isArray(pregunta.respuestas) || pregunta.respuestas.length !== 4) {
                setInfoMessage(`La pregunta ${i + 1} debe tener exactamente 4 respuestas.`);
                return false;
            }

            const correctas = pregunta.respuestas.filter(
                (respuesta) => Number(respuesta.Respuesta_Correcta) === 1
            );

            if (correctas.length !== 1) {
                setInfoMessage(`La pregunta ${i + 1} debe tener una sola respuesta correcta.`);
                return false;
            }

            for (let j = 0; j < pregunta.respuestas.length; j += 1) {
                if (!pregunta.respuestas[j].Respuesta_Texto.trim()) {
                    setInfoMessage(
                        `La respuesta ${j + 1} de la pregunta ${i + 1} no puede estar vacía.`
                    );
                    return false;
                }
            }
        }

        return true;
    };

    const handleSubmitVideo = async (e) => {
        e.preventDefault();
        resetMessages();

        const isValid = validateVideoForm();
        if (!isValid) return;

        try {
            setSavingVideo(true);

            const formData = new FormData();
            formData.append("Video_Titulo", videoForm.Video_Titulo.trim());
            formData.append("Instructor_ID", videoForm.Instructor_ID);
            formData.append("Video_Descripcion", videoForm.Video_Descripcion);
            formData.append("Video_URL_Vimeo", videoForm.Video_URL_Vimeo.trim());
            formData.append("Video_Duracion_Seg", videoForm.Video_Duracion_Seg.trim());
            formData.append("Video_Tiene_Evaluacion", videoForm.Video_Tiene_Evaluacion);
            formData.append("Video_Miniatura", videoForm.Video_Miniatura);
            formData.append("Video_Miniatura_Sitio", videoForm.Video_Miniatura_Sitio);
            formData.append("tags", JSON.stringify(selectedTags));

            if (Number(videoForm.Video_Tiene_Evaluacion) === 1) {
                formData.append("evaluacion", JSON.stringify(evaluacionForm));
            }

            const response = await fetch(`${API_BASE}/api/admin/videoList`, {
                method: "POST",
                credentials: "include",
                body: formData,
            });

            const data = await response.json();

            if (!response.ok) {
                if (response.status === 400 || response.status === 404) {
                    setInfoMessage(data?.message || "Verifica la información del formulario.");
                    return;
                }

                throw new Error(data?.message || "No fue posible agregar el video");
            }

            await loadVideos();

            setSuccessMessage(data?.message || "Se agregó el video con éxito.");
            resetVideoForm();
        } catch (error) {
            console.error("Error al agregar video:", error);
            setErrorMessage(error.message || "Ocurrió un error al agregar el video");
        } finally {
            setSavingVideo(false);
        }
    };

    // Funcion para ver Info del Video 
    const handleOpenViewVideo = async (video) => {
    if (!video?.Video_ID) return;

    try {
        setLoadingViewVideo(true);

        const response = await fetch(
            `${API_BASE}/api/admin/videoList/${video.Video_ID}`,
            {
                method: "GET",
                credentials: "include",
            }
        );

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data?.message || "No fue posible obtener el detalle del video");
        }

        setSelectedVideo(data?.data || null);
    } catch (error) {
        console.error("Error al cargar detalle del video:", error);
        setSelectedVideo(video || null);
    } finally {
        setLoadingViewVideo(false);
    }
};

    // Funcion para Editar info del Video 
    const [editVideoForm, setEditVideoForm] = useState({
        Video_ID: null,
        Video_Titulo: "",
        Instructor_ID: "",
        Video_Descripcion: "",
        Video_URL_Vimeo: "",
        Video_Duracion_Seg: "",
        Video_Tiene_Evaluacion: 0,
        Video_Miniatura: null,
        Video_Miniatura_Sitio: null,
        Current_Video_Miniatura: "",
        Current_Video_Miniatura_Sitio: "",
    });

    const [savingEditVideo, setSavingEditVideo] = useState(false);

    const [editSuccessMessage, setEditSuccessMessage] = useState("");
    const [editInfoMessage, setEditInfoMessage] = useState("");
    const [editErrorMessage, setEditErrorMessage] = useState("");

    const getEmptyEditRespuesta = (orden = 1) => ({
        Respuesta_Texto: "",
        Respuesta_Orden: orden,
        Respuesta_Correcta: 0,
    });

    const getEmptyEditPregunta = (orden = 1) => ({
        Pregunta_Texto: "",
        Pregunta_Orden: orden,
        Pregunta_Puntos_Estreno: 1,
        Pregunta_Puntos_Normal: 1,
        respuestas: [
            getEmptyEditRespuesta(1),
            getEmptyEditRespuesta(2),
            getEmptyEditRespuesta(3),
            getEmptyEditRespuesta(4),
        ],
    });

    const getInitialEditEvaluacionForm = (videoTitulo = "", videoDescripcion = "", preguntas = null) => ({
        Evaluacion_Titulo: videoTitulo?.trim()
            ? `Evaluación - ${videoTitulo.trim()}`
            : "Evaluación - ",
        Evaluacion_Descripcion: videoDescripcion || "",
        preguntas:
            Array.isArray(preguntas) && preguntas.length > 0
                ? preguntas
                : [getEmptyEditPregunta(1), getEmptyEditPregunta(2)],
    });

    const [editEvaluacionForm, setEditEvaluacionForm] = useState(
        getInitialEditEvaluacionForm("", "")
    );

    const resetEditMessages = () => {
        setEditSuccessMessage("");
        setEditInfoMessage("");
        setEditErrorMessage("");
    };

    const resetEditVideoForm = () => {
        setEditVideoForm({
            Video_ID: null,
            Video_Titulo: "",
            Instructor_ID: "",
            Video_Descripcion: "",
            Video_URL_Vimeo: "",
            Video_Duracion_Seg: "",
            Video_Tiene_Evaluacion: 0,
            Video_Miniatura: null,
            Video_Miniatura_Sitio: null,
            Current_Video_Miniatura: "",
            Current_Video_Miniatura_Sitio: "",
        });

        setEditSelectedTags([]);
        setEditEvaluacionForm(getInitialEditEvaluacionForm("", ""));

        const file1 = document.getElementById("editCustomFileVideo");
        const file2 = document.getElementById("editCustomFileVideoSite");

        if (file1) file1.value = "";
        if (file2) file2.value = "";
    };

    const normalizeEditPreguntas = (preguntas = []) => {
        if (!Array.isArray(preguntas) || preguntas.length === 0) {
            return [getEmptyEditPregunta(1), getEmptyEditPregunta(2)];
        }

        return preguntas.map((pregunta, preguntaIndex) => {
            let respuestas = Array.isArray(pregunta?.respuestas) ? [...pregunta.respuestas] : [];

            while (respuestas.length < 4) {
                respuestas.push(getEmptyEditRespuesta(respuestas.length + 1));
            }

            respuestas = respuestas.slice(0, 4).map((respuesta, respuestaIndex) => ({
                Respuesta_Texto: respuesta?.Respuesta_Texto || "",
                Respuesta_Orden: respuesta?.Respuesta_Orden || respuestaIndex + 1,
                Respuesta_Correcta: Number(respuesta?.Respuesta_Correcta) === 1 ? 1 : 0,
            }));

            const tieneCorrecta = respuestas.some(
                (respuesta) => Number(respuesta.Respuesta_Correcta) === 1
            );

            if (!tieneCorrecta && respuestas.length > 0) {
                respuestas[0].Respuesta_Correcta = 1;
            }

            return {
                Pregunta_Texto: pregunta?.Pregunta_Texto || "",
                Pregunta_Orden: pregunta?.Pregunta_Orden || preguntaIndex + 1,
                Pregunta_Puntos_Estreno:
                    pregunta?.Pregunta_Puntos_Estreno !== undefined
                        ? Number(pregunta.Pregunta_Puntos_Estreno)
                        : 1,
                Pregunta_Puntos_Normal:
                    pregunta?.Pregunta_Puntos_Normal !== undefined
                        ? Number(pregunta.Pregunta_Puntos_Normal)
                        : 1,
                respuestas,
            };
        });
    };

    const handleOpenEditVideo = async (video) => {
        if (!video?.Video_ID) return;

        try {
            resetEditMessages();
            setLoadingEditVideo(true);

            const response = await fetch(
                `${API_BASE}/api/admin/videoList/${video.Video_ID}`,
                {
                    method: "GET",
                    credentials: "include",
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data?.message || "No fue posible obtener el detalle del video");
            }

            const detail = data?.data;

            setEditSelectedTags(
                Array.isArray(detail?.tags)
                    ? detail.tags.map((tag) => Number(tag.Tag_ID))
                    : []
            );

            const preguntasNormalizadas = normalizeEditPreguntas(
                detail?.evaluacion?.preguntas || []
            );

            setEditVideoForm({
                Video_ID: detail.Video_ID,
                Video_Titulo: detail.Video_Titulo || "",
                Instructor_ID: detail.Instructor_ID || "",
                Video_Descripcion: detail.Video_Descripcion || "",
                Video_URL_Vimeo: detail.Video_URL_Vimeo || "",
                Video_Duracion_Seg: detail.Video_Duracion_Seg
                    ? String(detail.Video_Duracion_Seg)
                    : "",
                Video_Tiene_Evaluacion: Number(detail.Video_Tiene_Evaluacion) === 1 ? 1 : 0,
                Video_Miniatura: null,
                Video_Miniatura_Sitio: null,
                Current_Video_Miniatura: detail.Video_Miniatura || "",
                Current_Video_Miniatura_Sitio: detail.Video_Miniatura_Sitio || "",
            });

            setEditEvaluacionForm({
                Evaluacion_Titulo:
                    detail?.evaluacion?.Evaluacion_Titulo ||
                    (detail?.Video_Titulo?.trim()
                        ? `Evaluación - ${detail.Video_Titulo.trim()}`
                        : "Evaluación - "),
                Evaluacion_Descripcion:
                    detail?.evaluacion?.Evaluacion_Descripcion ||
                    detail?.Video_Descripcion ||
                    "",
                preguntas:
                    Number(detail?.Video_Tiene_Evaluacion) === 1
                        ? preguntasNormalizadas
                        : [getEmptyEditPregunta(1), getEmptyEditPregunta(2)],
            });
        } catch (error) {
            console.error("Error al abrir edición del video:", error);
            setEditErrorMessage(
                error.message || "Ocurrió un error al cargar el video para edición"
            );
        } finally {
            setLoadingEditVideo(false);
        }
    };

    useEffect(() => {
        if (Number(editVideoForm.Video_Tiene_Evaluacion) !== 1) return;

        setEditEvaluacionForm((prev) => ({
            ...prev,
            Evaluacion_Titulo: editVideoForm.Video_Titulo?.trim()
                ? `Evaluación - ${editVideoForm.Video_Titulo.trim()}`
                : "Evaluación - ",
            Evaluacion_Descripcion: editVideoForm.Video_Descripcion || "",
        }));
    }, [
        editVideoForm.Video_Tiene_Evaluacion,
        editVideoForm.Video_Titulo,
        editVideoForm.Video_Descripcion,
    ]);

    const handleEditVideoInputChange = (e) => {
        const { name, value, type, checked, files } = e.target;

        if (type === "file") {
            setEditVideoForm((prev) => ({
                ...prev,
                [name]: files?.[0] || null,
            }));
            return;
        }

        if (type === "checkbox") {
            const checkedValue = checked ? 1 : 0;

            setEditVideoForm((prev) => ({
                ...prev,
                [name]: checkedValue,
            }));

            if (name === "Video_Tiene_Evaluacion") {
                if (checkedValue === 1) {
                    setEditEvaluacionForm((prev) => {
                        const hasPreguntas =
                            Array.isArray(prev?.preguntas) && prev.preguntas.length > 0;

                        return hasPreguntas
                            ? {
                                ...prev,
                                Evaluacion_Titulo: editVideoForm.Video_Titulo?.trim()
                                    ? `Evaluación - ${editVideoForm.Video_Titulo.trim()}`
                                    : "Evaluación - ",
                                Evaluacion_Descripcion:
                                    editVideoForm.Video_Descripcion || "",
                            }
                            : getInitialEditEvaluacionForm(
                                editVideoForm.Video_Titulo,
                                editVideoForm.Video_Descripcion
                            );
                    });
                } else {
                    setEditEvaluacionForm(
                        getInitialEditEvaluacionForm(
                            editVideoForm.Video_Titulo,
                            editVideoForm.Video_Descripcion
                        )
                    );
                }
            }

            return;
        }

        setEditVideoForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleEditDescriptionChange = (value) => {
        setEditVideoForm((prev) => ({
            ...prev,
            Video_Descripcion: value,
        }));
    };

    const handleEditEvaluacionFieldChange = (e) => {
        const { name, value } = e.target;

        setEditEvaluacionForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleEditPreguntaChange = (preguntaIndex, field, value) => {
        setEditEvaluacionForm((prev) => {
            const preguntas = [...prev.preguntas];

            preguntas[preguntaIndex] = {
                ...preguntas[preguntaIndex],
                [field]:
                    field === "Pregunta_Orden" ||
                    field === "Pregunta_Puntos_Estreno" ||
                    field === "Pregunta_Puntos_Normal"
                        ? value === ""
                            ? ""
                            : Number(value)
                        : value,
            };

            return {
                ...prev,
                preguntas,
            };
        });
    };

    const handleEditRespuestaChange = (preguntaIndex, respuestaIndex, value) => {
        setEditEvaluacionForm((prev) => {
            const preguntas = [...prev.preguntas];
            const respuestas = [...preguntas[preguntaIndex].respuestas];

            respuestas[respuestaIndex] = {
                ...respuestas[respuestaIndex],
                Respuesta_Texto: value,
            };

            preguntas[preguntaIndex] = {
                ...preguntas[preguntaIndex],
                respuestas,
            };

            return {
                ...prev,
                preguntas,
            };
        });
    };

    const handleEditCorrectaChange = (preguntaIndex, respuestaIndex) => {
        setEditEvaluacionForm((prev) => {
            const preguntas = [...prev.preguntas];

            preguntas[preguntaIndex].respuestas = preguntas[preguntaIndex].respuestas.map(
                (respuesta, index) => ({
                    ...respuesta,
                    Respuesta_Correcta: index === respuestaIndex ? 1 : 0,
                })
            );

            return {
                ...prev,
                preguntas,
            };
        });
    };

    const addEditPregunta = () => {
        setEditEvaluacionForm((prev) => ({
            ...prev,
            preguntas: [
                ...prev.preguntas,
                getEmptyEditPregunta(prev.preguntas.length + 1),
            ],
        }));
    };

    const removeEditPregunta = (preguntaIndex) => {
        setEditEvaluacionForm((prev) => {
            const preguntas = prev.preguntas
                .filter((_, index) => index !== preguntaIndex)
                .map((pregunta, index) => ({
                    ...pregunta,
                    Pregunta_Orden: index + 1,
                }));

            return {
                ...prev,
                preguntas: preguntas.length > 0 ? preguntas : [getEmptyEditPregunta(1)],
            };
        });
    };

    const validateEditEvaluacionForm = () => {
        if (Number(editVideoForm.Video_Tiene_Evaluacion) !== 1) return true;

        if (!editEvaluacionForm.Evaluacion_Titulo.trim()) {
            setEditInfoMessage("El título de la evaluación es obligatorio.");
            return false;
        }

        if (
            !Array.isArray(editEvaluacionForm.preguntas) ||
            editEvaluacionForm.preguntas.length < 1
        ) {
            setEditInfoMessage("Debes agregar al menos una pregunta.");
            return false;
        }

        for (let i = 0; i < editEvaluacionForm.preguntas.length; i += 1) {
            const pregunta = editEvaluacionForm.preguntas[i];

            if (!pregunta.Pregunta_Texto.trim()) {
                setEditInfoMessage(`La pregunta ${i + 1} debe tener texto.`);
                return false;
            }

            if (
                pregunta.Pregunta_Orden === "" ||
                pregunta.Pregunta_Orden === null ||
                Number(pregunta.Pregunta_Orden) < 1
            ) {
                setEditInfoMessage(`La pregunta ${i + 1} debe tener un orden válido.`);
                return false;
            }

            if (
                pregunta.Pregunta_Puntos_Estreno === "" ||
                pregunta.Pregunta_Puntos_Estreno === null ||
                Number(pregunta.Pregunta_Puntos_Estreno) < 0
            ) {
                setEditInfoMessage(
                    `La pregunta ${i + 1} debe tener puntos de estreno válidos.`
                );
                return false;
            }

            if (
                pregunta.Pregunta_Puntos_Normal === "" ||
                pregunta.Pregunta_Puntos_Normal === null ||
                Number(pregunta.Pregunta_Puntos_Normal) < 0
            ) {
                setEditInfoMessage(
                    `La pregunta ${i + 1} debe tener puntos normales válidos.`
                );
                return false;
            }

            if (!Array.isArray(pregunta.respuestas) || pregunta.respuestas.length !== 4) {
                setEditInfoMessage(
                    `La pregunta ${i + 1} debe tener exactamente 4 respuestas.`
                );
                return false;
            }

            const correctas = pregunta.respuestas.filter(
                (respuesta) => Number(respuesta.Respuesta_Correcta) === 1
            );

            if (correctas.length !== 1) {
                setEditInfoMessage(
                    `La pregunta ${i + 1} debe tener una sola respuesta correcta.`
                );
                return false;
            }

            for (let j = 0; j < pregunta.respuestas.length; j += 1) {
                if (!pregunta.respuestas[j].Respuesta_Texto.trim()) {
                    setEditInfoMessage(
                        `La respuesta ${j + 1} de la pregunta ${i + 1} no puede estar vacía.`
                    );
                    return false;
                }
            }
        }

        return true;
    };

    const validateEditVideoForm = () => {
        if (!editVideoForm.Video_ID) {
            setEditErrorMessage("No se encontró el video a editar.");
            return false;
        }

        if (!editVideoForm.Video_Titulo.trim()) {
            setEditInfoMessage("El campo título es obligatorio.");
            return false;
        }

        if (!editVideoForm.Instructor_ID) {
            setEditInfoMessage("El campo experto es obligatorio.");
            return false;
        }

        if (
            !editVideoForm.Video_Descripcion ||
            !editVideoForm.Video_Descripcion.trim() ||
            editVideoForm.Video_Descripcion === "<p><br></p>"
        ) {
            setEditInfoMessage("El campo descripción es obligatorio.");
            return false;
        }

        if (!editVideoForm.Video_URL_Vimeo.trim()) {
            setEditInfoMessage("El campo ID Vimeo es obligatorio.");
            return false;
        }

        if (!editVideoForm.Video_Duracion_Seg.trim()) {
            setEditInfoMessage("El campo duración aproximada es obligatorio.");
            return false;
        }

        if (!/^\d+$/.test(editVideoForm.Video_Duracion_Seg.trim())) {
            setEditInfoMessage("La duración aproximada debe ser un número entero.");
            return false;
        }

        if (!validateEditEvaluacionForm()) {
            return false;
        }

        return true;
    };

    const handleSubmitEditVideo = async (e) => {
        e.preventDefault();
        resetEditMessages();

        const isValid = validateEditVideoForm();
        if (!isValid) return;

        try {
            setSavingEditVideo(true);

            const formData = new FormData();
            formData.append("Video_Titulo", editVideoForm.Video_Titulo.trim());
            formData.append("Instructor_ID", editVideoForm.Instructor_ID);
            formData.append("Video_Descripcion", editVideoForm.Video_Descripcion);
            formData.append("Video_URL_Vimeo", editVideoForm.Video_URL_Vimeo.trim());
            formData.append("Video_Duracion_Seg", editVideoForm.Video_Duracion_Seg.trim());
            formData.append("Video_Tiene_Evaluacion", editVideoForm.Video_Tiene_Evaluacion);
            formData.append("tags", JSON.stringify(editSelectedTags));

            if (editVideoForm.Video_Miniatura) {
                formData.append("Video_Miniatura", editVideoForm.Video_Miniatura);
            }

            if (editVideoForm.Video_Miniatura_Sitio) {
                formData.append("Video_Miniatura_Sitio", editVideoForm.Video_Miniatura_Sitio);
            }

            if (Number(editVideoForm.Video_Tiene_Evaluacion) === 1) {
                formData.append("evaluacion", JSON.stringify(editEvaluacionForm));
            }

            const response = await fetch(
                `${API_BASE}/api/admin/videoList/${editVideoForm.Video_ID}`,
                {
                    method: "PUT",
                    credentials: "include",
                    body: formData,
                }
            );

            const data = await response.json();

            if (!response.ok) {
                if (response.status === 400 || response.status === 404) {
                    setEditInfoMessage(
                        data?.message || "Verifica la información del formulario."
                    );
                    return;
                }

                throw new Error(data?.message || "No fue posible actualizar el video");
            }

            await loadVideos();

            if (selectedVideo?.Video_ID === editVideoForm.Video_ID) {
                setSelectedVideo(data?.data || null);
            }

            setEditSuccessMessage(
                data?.message || "El video fue actualizado correctamente."
            );
        } catch (error) {
            console.error("Error al actualizar video:", error);
            setEditErrorMessage(
                error.message || "Ocurrió un error al actualizar el video"
            );
        } finally {
            setSavingEditVideo(false);
        }
    };

    // Funcion para Desactivar video
    const handleDeleteVideo = async (videoId) => {
        if (!videoId) return;

        const result = await Swal.fire({
            title: "¿Desactivar video?",
            text: "Esta acción es irreversible y el video dejará de aparecer como activo.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#2f57ef",
            cancelButtonColor: "#d33",
            confirmButtonText: "Sí, desactivar",
            cancelButtonText: "Cancelar",
            reverseButtons: true,
            focusCancel: true,
        });

        if (!result.isConfirmed) return;

        try {
            const response = await fetch(
                `${API_BASE}/api/admin/videoList/${videoId}`,
                {
                    method: "DELETE",
                    credentials: "include",
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data?.message || "No fue posible desactivar el video");
            }

            await loadVideos();

            if (selectedVideo?.Video_ID === videoId) {
                setSelectedVideo(null);
            }

            await Swal.fire({
                title: "Correcto",
                text: data?.message || "El video fue desactivado correctamente.",
                icon: "success",
                confirmButtonText: "Aceptar",
            });

        } catch (error) {
            console.error("Error al desactivar video:", error);

            await Swal.fire({
                title: "Error",
                text: error.message || "Ocurrió un error al desactivar el video.",
                icon: "error",
                confirmButtonText: "Aceptar",
            });
        }
    };

    // Helpers

    const renderTagsBadges = (tags = []) => {
        if (!Array.isArray(tags) || tags.length === 0) {
            return <span className="text-muted">Sin tags</span>;
        }

        return (
            <div className="d-flex flex-wrap gap-2">
                {tags.map((tag) => (
                    <span
                        key={tag.Tag_ID || tag.VideoTagRel_ID}
                        className="badge"
                        style={{
                            backgroundColor: tag.Tag_Color || "#e9ecef",
                            color: "#212529",
                            padding: "0.5rem 0.75rem",
                        }}
                    >
                        {tag.Tag_Nombre}
                    </span>
                ))}
            </div>
        );
    };

    // Paginador
    const {
        currentPage,
        totalPages,
        totalItems,
        paginatedItems,
        visiblePages,
        startItem,
        endItem,
        goToPage,
        goToPreviousPage,
        goToNextPage,
        resetPagination,
        hasPreviousPage,
        hasNextPage,
    } = usePagination(videos, 8, 10);

    return (
        <>
        <Sidebar/>
        <main class="main-content">
            <Header/>
            <div class="content-inner container-fluid pb-0" id="page_layout">
                <div class="d-flex justify-content-between align-items-center flex-wrap mb-4 gap-3">
                    <div class="d-flex flex-column">
                        <h3>Lista de Videos</h3>
                        <p class="text-primary mb-0">Panduit Core</p>
                    </div>
                </div>

                <div class="row">
                    <div class="col-sm-12">
                        <div class="card">
                            <div class="card-header d-flex justify-content-end">
                                {/* <div class="header-title">
                                    <h4 class="card-title">Listado de Videos</h4>
                                </div> */}

                                <button class="btn btn-primary btn-sm" data-bs-toggle="modal" data-bs-target="#addVideoModal">
                                    <span class="btn-inner mr-5">
                                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="icon-24"><path d="M12 4V20M20 12H4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></svg>
                                    </span>
                                    Agregar Video
                                </button>
                            </div>

                            <div class="card-body px-0">
                                <div class="table-responsive">
                                    <table id="user-list-table" class="table table-striped" role="grid" data-toggle="data-table">
                                        <thead>
                                            <tr class="ligth">
                                            <th>ID</th>
                                            <th>Nombre</th>
                                            <th>Experto</th>
                                            <th>Estado</th>
                                            <th>Acción</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loadingVideos ? (
                                                <tr>
                                                    <td colSpan="5" className="text-center py-4">
                                                        Cargando videos...
                                                    </td>
                                                </tr>
                                            ) : paginatedItems.length === 0 ? (
                                                <tr>
                                                    <td colSpan="5" className="text-center py-4">
                                                        No hay videos registrados.
                                                    </td>
                                                </tr>
                                            ) : (
                                                paginatedItems.map((video) => (
                                                    <tr key={video.Video_ID}>
                                                        <td>{video.Video_ID}</td>

                                                        <td>{video.Video_Titulo}</td>

                                                        <td>
                                                            <div className="d-flex justify-content-start align-items-center">
                                                                <div className="pe-3">
                                                                    <img
                                                                        src={video.Instructor_Foto || "/assets/images/avatars/01.png"}
                                                                        className="rounded-circle bg-primary-subtle p-1 me-3"
                                                                        width="50"
                                                                        height="50"
                                                                        alt={video.Instructor_Nombre || "Instructor"}
                                                                        loading="lazy"
                                                                        style={{ objectFit: "cover" }}
                                                                    />
                                                                </div>
                                                                <div>
                                                                    <h6 className="mb-1">{video.Instructor_Nombre}</h6>
                                                                </div>
                                                            </div>
                                                        </td>

                                                        <td>{renderVideoStatus(video.Video_Activo)}</td>

                                                        <td>
                                                            <div className="d-flex">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-icon btn-info-subtle ms-2"
                                                                    id="viewVideoModalLabel"
                                                                    data-bs-toggle="modal"
                                                                    data-bs-target="#viewVideoModal"
                                                                    onClick={() => {
                                                                        handleOpenViewVideo(video); 
                                                                    }}
                                                                >
                                                                    <span className="btn-inner">
                                                                        <svg className="icon-32" width="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                            <path opacity="0.4" fillRule="evenodd" clipRule="evenodd" d="M17.7366 6.04606C19.4439 7.36388 20.8976 9.29455 21.9415 11.7091C22.0195 11.8924 22.0195 12.1067 21.9415 12.2812C19.8537 17.1103 16.1366 20 12 20H11.9902C7.86341 20 4.14634 17.1103 2.05854 12.2812C1.98049 12.1067 1.98049 11.8924 2.05854 11.7091C4.14634 6.87903 7.86341 4 11.9902 4H12C14.0683 4 16.0293 4.71758 17.7366 6.04606ZM8.09756 12C8.09756 14.1333 9.8439 15.8691 12 15.8691C14.1463 15.8691 15.8927 14.1333 15.8927 12C15.8927 9.85697 14.1463 8.12121 12 8.12121C9.8439 8.12121 8.09756 9.85697 8.09756 12Z" fill="currentColor"></path>
                                                                            <path d="M14.4308 11.997C14.4308 13.3255 13.3381 14.4115 12.0015 14.4115C10.6552 14.4115 9.5625 13.3255 9.5625 11.997C9.5625 11.8321 9.58201 11.678 9.61128 11.5228H9.66006C10.743 11.5228 11.621 10.6695 11.6601 9.60184C11.7674 9.58342 11.8845 9.57275 12.0015 9.57275C13.3381 9.57275 14.4308 10.6588 14.4308 11.997Z" fill="currentColor"></path>
                                                                        </svg>
                                                                    </span>
                                                                </button>

                                                                <button
                                                                    type="button"
                                                                    className="btn btn-icon btn-warning-subtle ms-2"
                                                                    id="editVideoModalLabel"
                                                                    data-bs-toggle="modal"
                                                                    data-bs-target="#editVideoModal"
                                                                    onClick={() => {
                                                                        // aquí luego colocas tu función para editar
                                                                        handleOpenEditVideo(video);
                                                                    }}
                                                                >
                                                                    <span className="btn-inner">
                                                                        <svg
                                                                            className="icon-32"
                                                                            width="32"
                                                                            viewBox="0 0 24 24"
                                                                            fill="none"
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                        >
                                                                            <path
                                                                                opacity="0.4"
                                                                                d="M19.9927 18.9534H14.2984C13.7429 18.9534 13.291 19.4124 13.291 19.9767C13.291 20.5422 13.7429 21.0001 14.2984 21.0001H19.9927C20.5483 21.0001 21.0001 20.5422 21.0001 19.9767C21.0001 19.4124 20.5483 18.9534 19.9927 18.9534Z"
                                                                                fill="currentColor"
                                                                            ></path>
                                                                            <path
                                                                                d="M10.309 6.90385L15.7049 11.2639C15.835 11.3682 15.8573 11.5596 15.7557 11.6929L9.35874 20.0282C8.95662 20.5431 8.36402 20.8344 7.72908 20.8452L4.23696 20.8882C4.05071 20.8903 3.88775 20.7613 3.84542 20.5764L3.05175 17.1258C2.91419 16.4915 3.05175 15.8358 3.45388 15.3306L9.88256 6.95545C9.98627 6.82108 10.1778 6.79743 10.309 6.90385Z"
                                                                                fill="currentColor"
                                                                            ></path>
                                                                            <path
                                                                                opacity="0.4"
                                                                                d="M18.1208 8.66544L17.0806 9.96401C16.9758 10.0962 16.7874 10.1177 16.6573 10.0124C15.3927 8.98901 12.1545 6.36285 11.2561 5.63509C11.1249 5.52759 11.1069 5.33625 11.2127 5.20295L12.2159 3.95706C13.126 2.78534 14.7133 2.67784 15.9938 3.69906L17.4647 4.87078C18.0679 5.34377 18.47 5.96726 18.6076 6.62299C18.7663 7.3443 18.597 8.0527 18.1208 8.66544Z"
                                                                                fill="currentColor"
                                                                            ></path>
                                                                        </svg>
                                                                    </span>
                                                                </button>

                                                                <button
                                                                    type="button"
                                                                    className="btn btn-icon btn-danger-subtle ms-2"
                                                                    onClick={() => handleDeleteVideo(video.Video_ID)}
                                                                >
                                                                    <span className="btn-inner">
                                                                        <svg
                                                                            className="icon-32"
                                                                            width="32"
                                                                            viewBox="0 0 24 24"
                                                                            fill="none"
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                        >
                                                                            <path
                                                                                opacity="0.4"
                                                                                d="M19.643 9.48851C19.643 9.5565 19.11 16.2973 18.8056 19.1342C18.615 20.8751 17.4927 21.9311 15.8092 21.9611C14.5157 21.9901 13.2494 22.0001 12.0036 22.0001C10.6809 22.0001 9.38741 21.9901 8.13185 21.9611C6.50477 21.9221 5.38147 20.8451 5.20057 19.1342C4.88741 16.2873 4.36418 9.5565 4.35445 9.48851C4.34473 9.28351 4.41086 9.08852 4.54507 8.93053C4.67734 8.78453 4.86796 8.69653 5.06831 8.69653H18.9388C19.1382 8.69653 19.3191 8.78453 19.4621 8.93053C19.5953 9.08852 19.6624 9.28351 19.643 9.48851Z"
                                                                                fill="currentColor"
                                                                            ></path>
                                                                            <path
                                                                                d="M21 5.97686C21 5.56588 20.6761 5.24389 20.2871 5.24389H17.3714C16.7781 5.24389 16.2627 4.8219 16.1304 4.22692L15.967 3.49795C15.7385 2.61698 14.9498 2 14.0647 2H9.93624C9.0415 2 8.26054 2.61698 8.02323 3.54595L7.87054 4.22792C7.7373 4.8219 7.22185 5.24389 6.62957 5.24389H3.71385C3.32386 5.24389 3 5.56588 3 5.97686V6.35685C3 6.75783 3.32386 7.08982 3.71385 7.08982H20.2871C20.6761 7.08982 21 6.75783 21 6.35685V5.97686Z"
                                                                                fill="currentColor"
                                                                            ></path>
                                                                        </svg>
                                                                    </span>
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <UniversalPagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                totalItems={totalItems}
                                startItem={startItem}
                                endItem={endItem}
                                visiblePages={visiblePages}
                                onPageChange={goToPage}
                                onPrevious={goToPreviousPage}
                                onNext={goToNextPage}
                                hasPreviousPage={hasPreviousPage}
                                hasNextPage={hasNextPage}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </main>

        {/* Modal para agregar Video */}
        <div
            className="modal fade"
            id="addVideoModal"
            tabIndex="-1"
            aria-labelledby="addVideoModalLabel"
            aria-hidden="true"
        >
            <div className="modal-dialog modal-dialog-centered modal-xl">
                <div className="modal-content">
                    <form onSubmit={handleSubmitVideo}>
                        <div className="modal-header">
                            <h5 className="modal-title" id="addVideoModalLabel">
                                Agregar Video
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                                onClick={() => {
                                    resetMessages();
                                    resetVideoForm();
                                }}
                            ></button>
                        </div>

                        <div className="modal-body">
                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <label className="form-label" htmlFor="titulo">
                                        Título: <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        id="titulo"
                                        name="Video_Titulo"
                                        type="text"
                                        className="form-control"
                                        value={videoForm.Video_Titulo}
                                        onChange={handleVideoInputChange}
                                        disabled={savingVideo}
                                    />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label" htmlFor="experto">
                                        Experto: <span className="text-danger">*</span>
                                    </label>
                                    <select
                                        id="experto"
                                        name="Instructor_ID"
                                        className="form-select"
                                        value={videoForm.Instructor_ID}
                                        onChange={handleVideoInputChange}
                                        disabled={savingVideo || loadingInstructors}
                                    >
                                        <option value="">
                                            {loadingInstructors
                                                ? "Cargando expertos..."
                                                : "Selecciona un experto"}
                                        </option>

                                        {instructors.map((instructor) => (
                                            <option
                                                key={instructor.Instructor_ID}
                                                value={instructor.Instructor_ID}
                                            >
                                                {instructor.Instructor_Nombre}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="row mb-3">
                                <div className="col">
                                    <label className="form-label">
                                        Descripción: <span className="text-danger">*</span>
                                    </label>

                                    <div className="border rounded video-quill-wrapper">
                                        <ReactQuill
                                            theme="snow"
                                            value={videoForm.Video_Descripcion}
                                            onChange={handleDescriptionChange}
                                            placeholder="Escribe la descripción del video..."
                                            readOnly={savingVideo}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <label className="form-label" htmlFor="customFileVideo">
                                        Selecciona una miniatura: <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        className="form-control"
                                        type="file"
                                        id="customFileVideo"
                                        name="Video_Miniatura"
                                        accept=".jpg,.jpeg,.png,.webp"
                                        onChange={handleVideoInputChange}
                                        disabled={savingVideo}
                                    />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label" htmlFor="customFileVideoSite">
                                        Selecciona una miniatura para el sitio: <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        className="form-control"
                                        type="file"
                                        id="customFileVideoSite"
                                        name="Video_Miniatura_Sitio"
                                        accept=".jpg,.jpeg,.png,.webp"
                                        onChange={handleVideoInputChange}
                                        disabled={savingVideo}
                                    />
                                </div>
                            </div>

                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <label className="form-label" htmlFor="vimeo">
                                        ID Vimeo: <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        id="vimeo"
                                        name="Video_URL_Vimeo"
                                        type="text"
                                        className="form-control"
                                        value={videoForm.Video_URL_Vimeo}
                                        onChange={handleVideoInputChange}
                                        disabled={savingVideo}
                                    />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label" htmlFor="duracion_aprox">
                                        Duración Aprox: <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        id="duracion_aprox"
                                        name="Video_Duracion_Seg"
                                        type="text"
                                        className="form-control"
                                        value={videoForm.Video_Duracion_Seg}
                                        onChange={handleVideoInputChange}
                                        disabled={savingVideo}
                                    />
                                </div>
                            </div>

                            <div className="row mb-4">
                                <div className="col-md-12">
                                    <label className="form-label">
                                        Tags del video
                                    </label>

                                    <div className="border rounded p-3 bg-light-subtle">
                                        {loadingTags ? (
                                            <p className="mb-0 text-muted">Cargando tags...</p>
                                        ) : tagsCatalog.length === 0 ? (
                                            <p className="mb-0 text-muted">No hay tags disponibles.</p>
                                        ) : (
                                            <div className="row">
                                                {tagsCatalog.map((tag) => (
                                                    <div className="col-md-4 mb-2" key={tag.Tag_ID}>
                                                        <div className="form-check">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                id={`tag-add-${tag.Tag_ID}`}
                                                                checked={selectedTags.includes(Number(tag.Tag_ID))}
                                                                onChange={() => handleTagToggle(Number(tag.Tag_ID))}
                                                                disabled={savingVideo}
                                                            />
                                                            <label
                                                                className="form-check-label"
                                                                htmlFor={`tag-add-${tag.Tag_ID}`}
                                                            >
                                                                <span
                                                                    className="badge me-2"
                                                                    style={{
                                                                        backgroundColor: tag.Tag_Color || "#e9ecef",
                                                                        color: "#212529",
                                                                    }}
                                                                >
                                                                    {tag.Tag_Tipo}
                                                                </span>
                                                                {tag.Tag_Nombre}
                                                            </label>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="row mb-4">
                                <div className="col-md-12">
                                    <div className="mb-3 form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="videoEvaluacion"
                                            name="Video_Tiene_Evaluacion"
                                            checked={videoForm.Video_Tiene_Evaluacion === 1}
                                            onChange={handleVideoInputChange}
                                            disabled={savingVideo}
                                        />
                                        <label className="form-check-label" htmlFor="videoEvaluacion">
                                            ¿Este video tendrá evaluación?
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {Number(videoForm.Video_Tiene_Evaluacion) === 1 && (
                                <div className="border rounded p-3 mb-4 bg-light-subtle">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5 className="mb-0">Configuración de evaluación</h5>
                                        <button
                                            type="button"
                                            className="btn btn-primary btn-sm"
                                            onClick={addPregunta}
                                            disabled={savingVideo}
                                        >
                                            Agregar pregunta
                                        </button>
                                    </div>

                                    <div className="row mb-3">
                                        <div className="col-md-6">
                                            <label className="form-label">
                                                Título de la evaluación
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="Evaluacion_Titulo"
                                                value={evaluacionForm.Evaluacion_Titulo}
                                                onChange={handleEvaluacionFieldChange}
                                                disabled={savingVideo}
                                            />
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label">
                                                Descripción de la evaluación
                                            </label>
                                            <textarea
                                                className="form-control"
                                                name="Evaluacion_Descripcion"
                                                rows="2"
                                                value={evaluacionForm.Evaluacion_Descripcion}
                                                onChange={handleEvaluacionFieldChange}
                                                disabled={savingVideo}
                                            />
                                        </div>
                                    </div>

                                    {evaluacionForm.preguntas.map((pregunta, preguntaIndex) => (
                                        <div
                                            key={`pregunta-${preguntaIndex}`}
                                            className="card mb-3 shadow-sm"
                                        >
                                            <div className="card-header d-flex justify-content-between align-items-center">
                                                <h6 className="mb-0">
                                                    Pregunta {preguntaIndex + 1}
                                                </h6>

                                                <button
                                                    type="button"
                                                    className="btn btn-danger btn-sm"
                                                    onClick={() => removePregunta(preguntaIndex)}
                                                    disabled={savingVideo}
                                                >
                                                    Eliminar
                                                </button>
                                            </div>

                                            <div className="card-body">
                                                <div className="row mb-3">
                                                    <div className="col-md-6">
                                                        <label className="form-label">
                                                            Texto de la pregunta
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={pregunta.Pregunta_Texto}
                                                            onChange={(e) =>
                                                                handlePreguntaChange(
                                                                    preguntaIndex,
                                                                    "Pregunta_Texto",
                                                                    e.target.value
                                                                )
                                                            }
                                                            disabled={savingVideo}
                                                        />
                                                    </div>

                                                    <div className="col-md-2">
                                                        <label className="form-label">Orden</label>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            min="1"
                                                            value={pregunta.Pregunta_Orden}
                                                            onChange={(e) =>
                                                                handlePreguntaChange(
                                                                    preguntaIndex,
                                                                    "Pregunta_Orden",
                                                                    e.target.value
                                                                )
                                                            }
                                                            disabled={savingVideo}
                                                        />
                                                    </div>

                                                    <div className="col-md-2">
                                                        <label className="form-label">
                                                            Puntos estreno
                                                        </label>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            min="0"
                                                            value={pregunta.Pregunta_Puntos_Estreno}
                                                            onChange={(e) =>
                                                                handlePreguntaChange(
                                                                    preguntaIndex,
                                                                    "Pregunta_Puntos_Estreno",
                                                                    e.target.value
                                                                )
                                                            }
                                                            disabled={savingVideo}
                                                        />
                                                    </div>

                                                    <div className="col-md-2">
                                                        <label className="form-label">
                                                            Puntos normal
                                                        </label>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            min="0"
                                                            value={pregunta.Pregunta_Puntos_Normal}
                                                            onChange={(e) =>
                                                                handlePreguntaChange(
                                                                    preguntaIndex,
                                                                    "Pregunta_Puntos_Normal",
                                                                    e.target.value
                                                                )
                                                            }
                                                            disabled={savingVideo}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="row">
                                                    {pregunta.respuestas.map((respuesta, respuestaIndex) => (
                                                        <div
                                                            key={`pregunta-${preguntaIndex}-respuesta-${respuestaIndex}`}
                                                            className="col-md-6 mb-3"
                                                        >
                                                            <label className="form-label">
                                                                Respuesta {respuestaIndex + 1}
                                                            </label>

                                                            <div className="input-group">
                                                                <span className="input-group-text">
                                                                    <input
                                                                        type="radio"
                                                                        name={`correcta-${preguntaIndex}`}
                                                                        checked={
                                                                            Number(respuesta.Respuesta_Correcta) === 1
                                                                        }
                                                                        onChange={() =>
                                                                            handleCorrectaChange(
                                                                                preguntaIndex,
                                                                                respuestaIndex
                                                                            )
                                                                        }
                                                                        disabled={savingVideo}
                                                                    />
                                                                </span>

                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    value={respuesta.Respuesta_Texto}
                                                                    onChange={(e) =>
                                                                        handleRespuestaChange(
                                                                            preguntaIndex,
                                                                            respuestaIndex,
                                                                            e.target.value
                                                                        )
                                                                    }
                                                                    placeholder={`Escribe la respuesta ${respuestaIndex + 1}`}
                                                                    disabled={savingVideo}
                                                                />
                                                            </div>

                                                            <small className="text-muted">
                                                                Marca el radio para indicar cuál es la correcta.
                                                            </small>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {successMessage && (
                                <div
                                    className="mb-3 alert alert-left alert-success alert-dismissible fade show"
                                    role="alert"
                                >
                                    <span>{successMessage}</span>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        aria-label="Close"
                                        onClick={() => setSuccessMessage("")}
                                    ></button>
                                </div>
                            )}

                            {infoMessage && (
                                <div
                                    className="mb-3 alert alert-top alert-info alert-dismissible fade show"
                                    role="alert"
                                >
                                    <span>{infoMessage}</span>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        aria-label="Close"
                                        onClick={() => setInfoMessage("")}
                                    ></button>
                                </div>
                            )}

                            {errorMessage && (
                                <div
                                    className="alert alert-bottom alert-danger alert-dismissible fade show"
                                    role="alert"
                                >
                                    <span>{errorMessage}</span>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        aria-label="Close"
                                        onClick={() => setErrorMessage("")}
                                    ></button>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-danger"
                                data-bs-dismiss="modal"
                                disabled={savingVideo}
                                onClick={() => {
                                    resetMessages();
                                    resetVideoForm();
                                }}
                            >
                                Cerrar
                            </button>

                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={savingVideo}
                            >
                                {savingVideo ? "Guardando..." : "Agregar"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        {/* Modal para Ver Video */}
        <div
            className="modal fade"
            id="viewVideoModal"
            tabIndex="-1"
            aria-labelledby="viewVideoModalLabel"
            aria-hidden="true"
        >
            <div className="modal-dialog modal-dialog-centered modal-xl">
                <div className="modal-content">
                    <div>
                        <div className="modal-header">
                            <h5 className="modal-title" id="viewVideoModalLabel">
                                Video
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                            ></button>
                        </div>

                        <div className="modal-body">
                            {loadingViewVideo ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Cargando...</span>
                                    </div>
                                    <p className="mt-3 mb-0">Cargando detalle del video...</p>
                                </div>
                            ) : (
                                <>
                                    <div className="row mb-3">
                                        <div className="col-md-12">
                                            <div className="bd-example text-center">
                                                <img
                                                    src={
                                                        selectedVideo?.Video_Miniatura ||
                                                        "../../assets/images/pro/plugins/img7.jpg"
                                                    }
                                                    className="img-fluid rounded effect-1"
                                                    alt={selectedVideo?.Video_Titulo || "Video"}
                                                    loading="lazy"
                                                    style={{
                                                        maxHeight: "350px",
                                                        width: "100%",
                                                        objectFit: "cover",
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row mb-3">
                                        <div className="col-md-12">
                                            <h4>{selectedVideo?.Video_Titulo || "Título del Video"}</h4>

                                            <label className="form-label me-4">
                                                Experto:{" "}
                                                <span className="text-primary">
                                                    {selectedVideo?.Instructor_Nombre || "Sin experto"}
                                                </span>
                                            </label>

                                            <label className="form-label">
                                                <svg
                                                    className="me-2"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="18"
                                                    height="18"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        fillRule="evenodd"
                                                        clipRule="evenodd"
                                                        d="M21.25 12.0005C21.25 17.1095 17.109 21.2505 12 21.2505C6.891 21.2505 2.75 17.1095 2.75 12.0005C2.75 6.89149 6.891 2.75049 12 2.75049C17.109 2.75049 21.25 6.89149 21.25 12.0005Z"
                                                        stroke="currentColor"
                                                        strokeWidth="1.5"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                    <path
                                                        d="M15.4316 14.9429L11.6616 12.6939V7.84692"
                                                        stroke="currentColor"
                                                        strokeWidth="1.5"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                                {selectedVideo?.Video_Duracion_Seg
                                                    ? `${selectedVideo.Video_Duracion_Seg} min`
                                                    : "Sin duración"}
                                            </label>

                                            <div className="mt-2">
                                                {Number(selectedVideo?.Video_Tiene_Evaluacion) === 1 ? (
                                                    <span className="badge bg-info-subtle text-info p-2">
                                                        Con evaluación
                                                    </span>
                                                ) : (
                                                    <span className="badge bg-secondary-subtle text-secondary p-2">
                                                        Sin evaluación
                                                    </span>
                                                )}

                                                {Number(selectedVideo?.Video_Activo) === 1 ? (
                                                    <span className="badge bg-success-subtle text-success p-2 ms-2">
                                                        Activo
                                                    </span>
                                                ) : (
                                                    <span className="badge bg-danger-subtle text-danger p-2 ms-2">
                                                        Inactivo
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row mb-4">
                                        <div className="col">
                                            <label className="form-label">
                                                Tags:
                                            </label>

                                            <div className="border rounded p-3">
                                                {renderTagsBadges(selectedVideo?.tags || [])}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row mb-4">
                                        <div className="col">
                                            <label className="form-label">
                                                Descripción:
                                            </label>

                                            <div
                                                className="video-description-view border rounded p-3"
                                                style={{ maxHeight: "300px", overflowY: "auto" }}
                                            >
                                                <div
                                                    dangerouslySetInnerHTML={{
                                                        __html:
                                                            selectedVideo?.Video_Descripcion ||
                                                            "<p>Sin descripción disponible.</p>",
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {Number(selectedVideo?.Video_Tiene_Evaluacion) === 1 && (
                                        <div className="row mb-3">
                                            <div className="col-12">
                                                    <div className="mb-3">
                                                        <h5 className="mb-1">Evaluación del video</h5>
                                                        {/* <p className="mb-1">
                                                            <strong>Título:</strong>{" "}
                                                            {selectedVideo?.evaluacion?.Evaluacion_Titulo ||
                                                                "Sin título"}
                                                        </p>
                                                        <p className="mb-0">
                                                            <strong>Descripción:</strong>{" "}
                                                            {selectedVideo?.evaluacion?.Evaluacion_Descripcion ||
                                                                "Sin descripción"}
                                                        </p> */}
                                                    </div>

                                                    {Array.isArray(selectedVideo?.evaluacion?.preguntas) &&
                                                    selectedVideo.evaluacion.preguntas.length > 0 ? (
                                                        selectedVideo.evaluacion.preguntas.map(
                                                            (pregunta, preguntaIndex) => (
                                                                <div
                                                                    key={pregunta.Pregunta_ID || preguntaIndex}
                                                                    className="card mb-3 shadow-sm"
                                                                >
                                                                    <div className="card-header">
                                                                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                                                                            <h6 className="mb-0">
                                                                                Pregunta {pregunta.Pregunta_Orden}
                                                                            </h6>

                                                                            <div className="d-flex flex-wrap gap-2">
                                                                                <span className="badge bg-warning-subtle text-warning p-2">
                                                                                    Puntos estreno:{" "}
                                                                                    {pregunta.Pregunta_Puntos_Estreno}
                                                                                </span>
                                                                                <span className="badge bg-primary-subtle text-primary p-2">
                                                                                    Puntos normal:{" "}
                                                                                    {pregunta.Pregunta_Puntos_Normal}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    <div className="card-body">
                                                                        <p className="fw-semibold mb-3">
                                                                            {pregunta.Pregunta_Texto}
                                                                        </p>

                                                                        <div className="row">
                                                                            {Array.isArray(pregunta.respuestas) &&
                                                                                pregunta.respuestas.map(
                                                                                    (respuesta, respuestaIndex) => (
                                                                                        <div
                                                                                            className="col-md-6 mb-3"
                                                                                            key={
                                                                                                respuesta.Respuesta_ID ||
                                                                                                `${preguntaIndex}-${respuestaIndex}`
                                                                                            }
                                                                                        >
                                                                                            <div
                                                                                                className={`border rounded p-3 h-100 ${
                                                                                                    Number(
                                                                                                        respuesta.Respuesta_Correcta
                                                                                                    ) === 1
                                                                                                        ? "border-success bg-success-subtle"
                                                                                                        : "border-light bg-white"
                                                                                                }`}
                                                                                            >
                                                                                                <div className="d-flex justify-content-between align-items-start gap-2">
                                                                                                    <div>
                                                                                                        <strong>
                                                                                                            Respuesta{" "}
                                                                                                            {
                                                                                                                respuesta.Respuesta_Orden
                                                                                                            }
                                                                                                        </strong>
                                                                                                        <p className="mb-0 mt-1">
                                                                                                            {
                                                                                                                respuesta.Respuesta_Texto
                                                                                                            }
                                                                                                        </p>
                                                                                                    </div>

                                                                                                    {Number(
                                                                                                        respuesta.Respuesta_Correcta
                                                                                                    ) === 1 && (
                                                                                                        <span className="badge bg-success">
                                                                                                            Correcta
                                                                                                        </span>
                                                                                                    )}
                                                                                                </div>
                                                                                            </div>
                                                                                        </div>
                                                                                    )
                                                                                )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )
                                                        )
                                                    ) : (
                                                        <div className="alert alert-secondary mb-0">
                                                            Este video tiene evaluación marcada, pero no se encontraron preguntas registradas.
                                                        </div>
                                                    )}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-warning"
                                id="editVideoModalLabel"
                                data-bs-toggle="modal"
                                data-bs-target="#editVideoModal"
                                onClick={() => selectedVideo && handleOpenEditVideo(selectedVideo)}
                            >
                                Editar
                            </button>

                            <button
                                type="button"
                                className="btn btn-primary"
                                data-bs-dismiss="modal"
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        {/* Modal para editar Video */}
        <div
            className="modal fade"
            id="editVideoModal"
            tabIndex="-1"
            aria-labelledby="editVideoModalLabel"
            aria-hidden="true"
        >
            <div className="modal-dialog modal-dialog-centered modal-xl">
                <div className="modal-content">
                    <form onSubmit={handleSubmitEditVideo}>
                        <div className="modal-header">
                            <h5 className="modal-title" id="editVideoModalLabel">
                                Editar Video
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                                onClick={() => {
                                    resetEditMessages();
                                }}
                            ></button>
                        </div>

                        <div className="modal-body">
                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <label className="form-label" htmlFor="editTitulo">
                                        Título: <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        id="editTitulo"
                                        name="Video_Titulo"
                                        type="text"
                                        className="form-control"
                                        value={editVideoForm.Video_Titulo}
                                        onChange={handleEditVideoInputChange}
                                        disabled={savingEditVideo}
                                    />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label" htmlFor="editExperto">
                                        Experto: <span className="text-danger">*</span>
                                    </label>
                                    <select
                                        id="editExperto"
                                        name="Instructor_ID"
                                        className="form-select"
                                        value={editVideoForm.Instructor_ID}
                                        onChange={handleEditVideoInputChange}
                                        disabled={savingEditVideo || loadingInstructors}
                                    >
                                        <option value="">
                                            {loadingInstructors
                                                ? "Cargando expertos..."
                                                : "Selecciona un experto"}
                                        </option>

                                        {instructors.map((instructor) => (
                                            <option
                                                key={instructor.Instructor_ID}
                                                value={instructor.Instructor_ID}
                                            >
                                                {instructor.Instructor_Nombre}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="row mb-3">
                                <div className="col">
                                    <label className="form-label">
                                        Descripción: <span className="text-danger">*</span>
                                    </label>

                                    <div className="border rounded video-quill-wrapper">
                                        <ReactQuill
                                            theme="snow"
                                            value={editVideoForm.Video_Descripcion}
                                            onChange={handleEditDescriptionChange}
                                            placeholder="Escribe la descripción del video..."
                                            readOnly={savingEditVideo}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="row mb-2">
                                <div className="col-md-6">
                                    {editVideoForm.Current_Video_Miniatura && (
                                        <div className="mb-2">
                                            <small className="text-muted d-block mb-1">
                                                Miniatura actual
                                            </small>
                                            <img
                                                src={editVideoForm.Current_Video_Miniatura}
                                                alt="Miniatura actual"
                                                className="img-fluid rounded border"
                                                style={{ maxHeight: "120px", objectFit: "cover" }}
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="col-md-6">
                                    {editVideoForm.Current_Video_Miniatura_Sitio && (
                                        <div className="mb-2">
                                            <small className="text-muted d-block mb-1">
                                                Miniatura actual del sitio
                                            </small>
                                            <img
                                                src={editVideoForm.Current_Video_Miniatura_Sitio}
                                                alt="Miniatura actual del sitio"
                                                className="img-fluid rounded border"
                                                style={{ maxHeight: "120px", objectFit: "cover" }}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <label className="form-label" htmlFor="editCustomFileVideo">
                                        Selecciona una miniatura nueva
                                    </label>
                                    <input
                                        className="form-control"
                                        type="file"
                                        id="editCustomFileVideo"
                                        name="Video_Miniatura"
                                        accept=".jpg,.jpeg,.png,.webp"
                                        onChange={handleEditVideoInputChange}
                                        disabled={savingEditVideo}
                                    />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label" htmlFor="editCustomFileVideoSite">
                                        Selecciona una miniatura nueva para el sitio
                                    </label>
                                    <input
                                        className="form-control"
                                        type="file"
                                        id="editCustomFileVideoSite"
                                        name="Video_Miniatura_Sitio"
                                        accept=".jpg,.jpeg,.png,.webp"
                                        onChange={handleEditVideoInputChange}
                                        disabled={savingEditVideo}
                                    />
                                </div>
                            </div>

                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <label className="form-label" htmlFor="editVimeo">
                                        ID Vimeo: <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        id="editVimeo"
                                        name="Video_URL_Vimeo"
                                        type="text"
                                        className="form-control"
                                        value={editVideoForm.Video_URL_Vimeo}
                                        onChange={handleEditVideoInputChange}
                                        disabled={savingEditVideo}
                                    />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label" htmlFor="editDuracionAprox">
                                        Duración Aprox: <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        id="editDuracionAprox"
                                        name="Video_Duracion_Seg"
                                        type="text"
                                        className="form-control"
                                        value={editVideoForm.Video_Duracion_Seg}
                                        onChange={handleEditVideoInputChange}
                                        disabled={savingEditVideo}
                                    />
                                </div>
                            </div>

                            <div className="row mb-4">
                                <div className="col-md-12">
                                    <label className="form-label">
                                        Tags del video
                                    </label>

                                    <div className="border rounded p-3 bg-light-subtle">
                                        {loadingTags ? (
                                            <p className="mb-0 text-muted">Cargando tags...</p>
                                        ) : tagsCatalog.length === 0 ? (
                                            <p className="mb-0 text-muted">No hay tags disponibles.</p>
                                        ) : (
                                            <div className="row">
                                                {tagsCatalog.map((tag) => (
                                                    <div className="col-md-4 mb-2" key={tag.Tag_ID}>
                                                        <div className="form-check">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                id={`tag-edit-${tag.Tag_ID}`}
                                                                checked={editSelectedTags.includes(Number(tag.Tag_ID))}
                                                                onChange={() => handleEditTagToggle(Number(tag.Tag_ID))}
                                                                disabled={savingEditVideo}
                                                            />
                                                            <label
                                                                className="form-check-label"
                                                                htmlFor={`tag-edit-${tag.Tag_ID}`}
                                                            >
                                                                <span
                                                                    className="badge me-2"
                                                                    style={{
                                                                        backgroundColor: tag.Tag_Color || "#e9ecef",
                                                                        color: "#212529",
                                                                    }}
                                                                >
                                                                    {tag.Tag_Tipo}
                                                                </span>
                                                                {tag.Tag_Nombre}
                                                            </label>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="row mb-4">
                                <div className="col-md-12">
                                    <div className="mb-3 form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="editVideoEvaluacion"
                                            name="Video_Tiene_Evaluacion"
                                            checked={editVideoForm.Video_Tiene_Evaluacion === 1}
                                            onChange={handleEditVideoInputChange}
                                            disabled={savingEditVideo}
                                        />
                                        <label className="form-check-label" htmlFor="editVideoEvaluacion">
                                            ¿Este video tendrá evaluación?
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {Number(editVideoForm.Video_Tiene_Evaluacion) === 1 && (
                                <div className="border rounded p-3 mb-4 bg-light-subtle">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5 className="mb-0">Configuración de evaluación</h5>
                                        <button
                                            type="button"
                                            className="btn btn-primary btn-sm"
                                            onClick={addEditPregunta}
                                            disabled={savingEditVideo}
                                        >
                                            Agregar pregunta
                                        </button>
                                    </div>

                                    {/* <div className="row mb-3">
                                        <div className="col-md-6">
                                            <label className="form-label">
                                                Título de la evaluación
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="Evaluacion_Titulo"
                                                value={editEvaluacionForm.Evaluacion_Titulo}
                                                onChange={handleEditEvaluacionFieldChange}
                                                disabled={savingEditVideo}
                                            />
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label">
                                                Descripción de la evaluación
                                            </label>
                                            <textarea
                                                className="form-control"
                                                name="Evaluacion_Descripcion"
                                                rows="2"
                                                value={editEvaluacionForm.Evaluacion_Descripcion}
                                                onChange={handleEditEvaluacionFieldChange}
                                                disabled={savingEditVideo}
                                            />
                                        </div>
                                    </div> */}

                                    {editEvaluacionForm.preguntas.map((pregunta, preguntaIndex) => (
                                        <div
                                            key={`edit-pregunta-${preguntaIndex}`}
                                            className="card mb-3 shadow-sm"
                                        >
                                            <div className="card-header d-flex justify-content-between align-items-center">
                                                <h6 className="mb-0">
                                                    Pregunta {preguntaIndex + 1}
                                                </h6>

                                                <button
                                                    type="button"
                                                    className="btn btn-danger btn-sm"
                                                    onClick={() => removeEditPregunta(preguntaIndex)}
                                                    disabled={savingEditVideo}
                                                >
                                                    Eliminar
                                                </button>
                                            </div>

                                            <div className="card-body">
                                                <div className="row mb-3">
                                                    <div className="col-md-6">
                                                        <label className="form-label">
                                                            Texto de la pregunta
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={pregunta.Pregunta_Texto}
                                                            onChange={(e) =>
                                                                handleEditPreguntaChange(
                                                                    preguntaIndex,
                                                                    "Pregunta_Texto",
                                                                    e.target.value
                                                                )
                                                            }
                                                            disabled={savingEditVideo}
                                                        />
                                                    </div>

                                                    <div className="col-md-2">
                                                        <label className="form-label">Orden</label>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            min="1"
                                                            value={pregunta.Pregunta_Orden}
                                                            onChange={(e) =>
                                                                handleEditPreguntaChange(
                                                                    preguntaIndex,
                                                                    "Pregunta_Orden",
                                                                    e.target.value
                                                                )
                                                            }
                                                            disabled={savingEditVideo}
                                                        />
                                                    </div>

                                                    <div className="col-md-2">
                                                        <label className="form-label">
                                                            Puntos estreno
                                                        </label>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            min="0"
                                                            value={pregunta.Pregunta_Puntos_Estreno}
                                                            onChange={(e) =>
                                                                handleEditPreguntaChange(
                                                                    preguntaIndex,
                                                                    "Pregunta_Puntos_Estreno",
                                                                    e.target.value
                                                                )
                                                            }
                                                            disabled={savingEditVideo}
                                                        />
                                                    </div>

                                                    <div className="col-md-2">
                                                        <label className="form-label">
                                                            Puntos normal
                                                        </label>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            min="0"
                                                            value={pregunta.Pregunta_Puntos_Normal}
                                                            onChange={(e) =>
                                                                handleEditPreguntaChange(
                                                                    preguntaIndex,
                                                                    "Pregunta_Puntos_Normal",
                                                                    e.target.value
                                                                )
                                                            }
                                                            disabled={savingEditVideo}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="row">
                                                    {pregunta.respuestas.map((respuesta, respuestaIndex) => (
                                                        <div
                                                            key={`edit-pregunta-${preguntaIndex}-respuesta-${respuestaIndex}`}
                                                            className="col-md-6 mb-3"
                                                        >
                                                            <label className="form-label">
                                                                Respuesta {respuestaIndex + 1}
                                                            </label>

                                                            <div className="input-group">
                                                                <span className="input-group-text">
                                                                    <input
                                                                        type="radio"
                                                                        name={`edit-correcta-${preguntaIndex}`}
                                                                        checked={
                                                                            Number(respuesta.Respuesta_Correcta) === 1
                                                                        }
                                                                        onChange={() =>
                                                                            handleEditCorrectaChange(
                                                                                preguntaIndex,
                                                                                respuestaIndex
                                                                            )
                                                                        }
                                                                        disabled={savingEditVideo}
                                                                    />
                                                                </span>

                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    value={respuesta.Respuesta_Texto}
                                                                    onChange={(e) =>
                                                                        handleEditRespuestaChange(
                                                                            preguntaIndex,
                                                                            respuestaIndex,
                                                                            e.target.value
                                                                        )
                                                                    }
                                                                    placeholder={`Escribe la respuesta ${respuestaIndex + 1}`}
                                                                    disabled={savingEditVideo}
                                                                />
                                                            </div>

                                                            <small className="text-muted">
                                                                Marca el radio para indicar cuál es la correcta.
                                                            </small>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {editSuccessMessage && (
                                <div
                                    className="mb-3 alert alert-left alert-success alert-dismissible fade show"
                                    role="alert"
                                >
                                    <span>{editSuccessMessage}</span>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        aria-label="Close"
                                        onClick={() => setEditSuccessMessage("")}
                                    ></button>
                                </div>
                            )}

                            {editInfoMessage && (
                                <div
                                    className="mb-3 alert alert-top alert-info alert-dismissible fade show"
                                    role="alert"
                                >
                                    <span>{editInfoMessage}</span>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        aria-label="Close"
                                        onClick={() => setEditInfoMessage("")}
                                    ></button>
                                </div>
                            )}

                            {editErrorMessage && (
                                <div
                                    className="alert alert-bottom alert-danger alert-dismissible fade show"
                                    role="alert"
                                >
                                    <span>{editErrorMessage}</span>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        aria-label="Close"
                                        onClick={() => setEditErrorMessage("")}
                                    ></button>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-danger"
                                data-bs-dismiss="modal"
                                disabled={savingEditVideo}
                                onClick={() => {
                                    resetEditMessages();
                                    resetEditVideoForm();
                                }}
                            >
                                Cerrar
                            </button>

                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={savingEditVideo}
                            >
                                {savingEditVideo ? "Guardando..." : "Guardar cambios"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
        
        </>
    )
}

export default VideoList